import React from 'react';
import { Drawer, Toolbar } from '@mui/material';
import { MainLayoutType } from '@uplift/types';
import { MainLayoutContext } from './main-layout.provider';
import { SideBar } from './SideBar';
import { makeStyles } from 'tss-react/mui';


export const useStyles = makeStyles<Partial<UpliftDrawerProps>>()((theme, props) => ({
  drawer: {
    flexShrink: 0,
    width: '0px',
    zIndex: theme.zIndex.appBar,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  drawerOpened: {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.up('sm')]: {
      width: props.drawerWidth || 240,
    }
  },
  drawerPaper: {
    width: props.drawerWidth || 240,
  },
}));


export type UpliftDrawerProps = Pick<MainLayoutType, 'useLayoutConf'> & {
  initialIsDrawerOpened: boolean;
  drawerWidth?: number;
};

export const UpliftDrawer: React.FC<UpliftDrawerProps> = ({ initialIsDrawerOpened, useLayoutConf, drawerWidth = 240 }) => {
  const { isDrawerOpened, setIsDrawerOpened, isMobile } = React.useContext(MainLayoutContext);
  const { infoNavConfig, mainNavConfig } = useLayoutConf();
  const { classes, cx } = useStyles({ drawerWidth });

  const toggleSideBar = React.useCallback(() => {
    setIsDrawerOpened(!isDrawerOpened);
  }, [ isDrawerOpened ]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (isMobile && initialIsDrawerOpened) {
      return;
    }
    setIsDrawerOpened(initialIsDrawerOpened);
  }, [ initialIsDrawerOpened, isMobile ]); // eslint-disable-line react-hooks/exhaustive-deps

  const slideProps = React.useMemo(() => {
    const handleResize = () => {
      window.dispatchEvent(new Event('resize'));
    };

    return {
      onExited: handleResize,
      onEntered: handleResize,
    };
  }, []);

  return (
    <Drawer
      className={cx(classes.drawer, { [classes.drawerOpened]: isDrawerOpened })}
      variant="persistent"
      anchor="left"
      open={isDrawerOpened}
      classes={{
        paper: classes.drawerPaper,
      }}
      SlideProps={slideProps}
    >
      <Toolbar />
      <SideBar
        infoNavConfig={infoNavConfig}
        mainNavConfig={mainNavConfig}
        toggleSideBar={toggleSideBar}
      />
    </Drawer>
  );
};
